/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useRef, useState, ChangeEvent } from "react";
import { Input, theme, FlexContainer } from "@nordcloud/gnui";
import { Sidebar } from "~/components/Sidebar";
import { LoaderWrap } from "../Loader";
import { SidebarItemList } from "./SidebarItemList";
import { Item } from "./types";

type Props = {
  options?: Item[];
  sideBarTitle?: string;
  itemsTitle?: string;
  onClick: () => void;
  isOpen: boolean;
  loading: boolean;
  placeholder?: string;
  onSubmit: (item: Item) => void;
};

export function SidebarSelector({
  options = [],
  sideBarTitle,
  itemsTitle,
  isOpen,
  loading,
  onClick,
  onSubmit,
}: Props) {
  const [filteredOptions, setFilteredOptions] = useState<Item[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFilteredOptions(options);

    const inputRefCurrent = inputRef?.current;
    if (isOpen && inputRefCurrent) {
      inputRefCurrent.focus();
    }
  }, [options, isOpen]);

  const filterBySearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setFilteredOptions(
      options.filter(
        ({ label, value }) =>
          label.toLowerCase().includes(searchValue) ||
          value.toLowerCase().includes(searchValue)
      )
    );
  };

  return (
    <Sidebar title={sideBarTitle} isOpen={isOpen} onClick={onClick}>
      <FlexContainer mb={theme.spacing.spacing05}>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search"
          onChange={filterBySearch}
        />
      </FlexContainer>
      <LoaderWrap inContent loading={loading}>
        <SidebarItemList
          {...{ itemsTitle, onSubmit }}
          items={filteredOptions}
        />
      </LoaderWrap>
    </Sidebar>
  );
}
